import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Bookmark, ChevronRight, ChevronLeft, Star } from 'lucide-react';

const HeroCarousel = ({ courses, onPlay }) => {
  // Filtramos los cursos destacados (máximo 5)
  const featured = courses.filter(c => c.featured).slice(0, 5);
  const [current, setCurrent] = useState(0);

  // Cambio automático de slide cada 8 segundos
  useEffect(() => {
    if (featured.length === 0) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev === featured.length - 1 ? 0 : prev + 1));
    }, 8000);
    return () => clearInterval(timer);
  }, [featured.length]);

  if (featured.length === 0) return null;

  return (
    <section className="relative w-full h-[550px] md:h-[80vh] lg:h-[90vh] bg-[#050505] overflow-hidden md:rounded-[40px] md:mx-auto md:max-w-[95%] shadow-2xl border-b md:border border-white/5">
      
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          
          <motion.div 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, ease: "easeOut" }}
            className="absolute inset-0 z-0"
          >
            <img 
              src={featured[current].image} 
              className="w-full h-full object-cover object-center md:object-[center_20%]" 
              alt={featured[current].title}
            />
            
            
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          </motion.div>

          
          <div className="relative z-10 max-w-7xl mx-auto h-full px-6 flex items-center">
            {/* pt-32 para evitar que el logo en móvil tape el texto */}
            <div className="max-w-2xl space-y-4 md:space-y-6 pt-32 md:pt-20">
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="flex items-center gap-3"
              >
                <span className="px-2 py-0.5 md:px-3 md:py-1 bg-blue-600 text-white text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] rounded">
                    Recomendado
                </span>
                <div className="flex items-center gap-1 text-yellow-500 font-bold text-xs md:text-sm bg-black/40 px-2 py-1 rounded-md backdrop-blur-md">
                    <Star size={14} fill="currentColor" />
                    <span>{featured[current].rating || "4.9"}</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white leading-[1.1] md:leading-[0.9] tracking-tighter"
              >
                {featured[current].title}
                <span className="text-blue-500">.</span>
              </motion.h1>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-sm md:text-xl text-slate-200 font-medium max-w-lg leading-relaxed drop-shadow-lg line-clamp-2 md:line-clamp-none"
              >
                {featured[current].description}
              </motion.p>

              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-3 md:gap-4 pt-2 md:pt-4"
              >
                <button 
                  onClick={() => onPlay(featured[current])}
                  className="group flex items-center gap-2 md:gap-3 px-6 py-3 md:px-10 md:py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl md:rounded-2xl font-black text-[10px] md:text-sm uppercase tracking-widest transition-all shadow-lg hover:scale-105 active:scale-95"
                >
                  <Play fill="currentColor" size={18} />
                  Ver ahora
                </button>
                <button className="p-3 md:p-5 bg-black/40 border border-white/10 text-white rounded-xl md:rounded-2xl hover:bg-white/10 backdrop-blur-md transition-all">
                  <Bookmark size={20} className="md:w-6 md:h-6" />
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      
      <div className="absolute bottom-6 left-6 right-6 md:bottom-10 md:right-10 z-20 flex flex-col md:flex-row items-center justify-between md:justify-end gap-6">
        
        <div className="flex gap-2">
            {featured.map((_, idx) => (
                <button
                    key={idx}
                    onClick={() => setCurrent(idx)}
                    className={`h-1 md:h-1.5 transition-all duration-500 rounded-full ${
                        idx === current ? 'w-8 md:w-10 bg-blue-500' : 'w-2 bg-white/30 hover:bg-white/60'
                    }`}
                />
            ))}
        </div>

        
        <div className="flex gap-2">
            <button 
                onClick={() => setCurrent(current === 0 ? featured.length - 1 : current - 1)}
                className="p-3 md:p-2 bg-black/40 border border-white/10 rounded-full text-white hover:bg-white/10 backdrop-blur-md transition-all"
            >
                <ChevronLeft size={20} />
            </button>
            <button 
                onClick={() => setCurrent(current === featured.length - 1 ? 0 : current + 1)}
                className="p-3 md:p-2 bg-black/40 border border-white/10 rounded-full text-white hover:bg-white/10 backdrop-blur-md transition-all"
            >
                <ChevronRight size={20} />
            </button>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;